import type { CSSProperties, PropsWithChildren } from 'react';
import { fontFamilyStackCss } from '@/document-render/application/blockFontSize';
import { getPageDimensions } from '../domain/PageSize';
import type { PageSize } from '../domain/PageSize';
import type { TemplateBackground } from '@/templates/domain/types/TemplateBackground';
import type { TemplateLogo } from '@/templates/domain/types/TemplateLogo';
import { resolvePageBackgroundStyle } from '../application/resolvePageBackgroundStyle';
import { BACKGROUND_COLORS, RGBA_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { CompanyLogoSlot } from './CompanyLogoSlot';
import { DocumentSheetTokens } from './DocumentSheetTokens';

interface Props extends PropsWithChildren {
  pageNumber: number;
  totalPages: number;
  pageSize: PageSize;
  pagePadding: number;
  /** Template page background styling. */
  background?: TemplateBackground;
  /** True when this page hosts a cover_page block. */
  isCoverPage?: boolean;
  /** Template base font applied as CSS `font-family` on every page. */
  baseFont?: string;
  /** Template company logo placement (header/footer). */
  logo?: TemplateLogo | null;
  /** Resolved logo URL (template or context). Absent → no logo chrome. */
  logoUrl?: string | null;
  /**
   * When true, a header block is present on this document so header logo renders inside
   * the header block — skip page-chrome header logo to avoid double-render.
   * Cover pages also skip header chrome (cover_page.showLogo owns the logo).
   */
  hasHeaderBlock?: boolean;
  layoutMode?: 'flow' | 'fixed';
  /**
   * CSS scale when the host container is narrower than the page.
   * Keeps layout/measurement at full page width (A4) while fitting the viewport.
   * Pass 1 (or omit) when the host is already wide enough — print/PDF paths that
   * do not use this HTML surface are unaffected.
   */
  scale?: number;
}

export function PageRender({
  children,
  pageNumber,
  pageSize,
  pagePadding,
  background,
  isCoverPage = false,
  baseFont,
  logo,
  logoUrl,
  hasHeaderBlock = false,
  layoutMode = 'flow',
  scale = 1
}: Props) {
  const { widthMm, heightMm } = getPageDimensions(pageSize, 'portrait');
  const shouldScale = scale > 0 && scale < 1;
  const fontFamily = fontFamilyStackCss(baseFont?.trim() || 'Inter');
  const pageBackgroundStyle = resolvePageBackgroundStyle({
    background,
    isCoverPage
  });

  const hasLogo = Boolean(logoUrl?.trim());
  // Header logo chrome when position=header and no header block (DB-2). Header block owns logo otherwise.
  // Cover pages own the logo via cover_page.showLogo — skip page chrome to avoid a stacked duplicate.
  const showHeaderLogo = hasLogo && logo?.position === 'header' && !hasHeaderBlock && !isCoverPage;
  const showFooterLogo = hasLogo && logo?.position === 'footer';

  // Distinct paper sheet so multi-page previews show clear separation (not one continuous strip).
  const paperMarginBottom = 24;
  const paperBorder = `1px solid ${RGBA_COLORS.COMPOSITION_BORDER}`;
  const paperShadow = RGBA_COLORS.PAGE_PAPER_SHADOW;

  const sectionStyle: CSSProperties = {
    width: shouldScale ? `${widthMm}mm` : '100%',
    maxWidth: `${widthMm}mm`,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: paperMarginBottom,
    padding: `${pagePadding}px`,
    boxSizing: 'border-box',
    // Fixed (export / LiveDocumentPreview) pages must clip — otherwise overfull
    // content paints past the A4 frame onto the gray canvas.
    overflow: layoutMode === 'fixed' ? 'hidden' : 'visible',
    fontFamily,
    // The CRM shell's body line-height is an absolute 24px. Without a relative
    // leading here, 9–13px quote text inherits that and prints about twice as
    // loose as the source. The block style model has no line-height field.
    lineHeight: 1.15,
    // The sheet is white whatever the host theme is, so its text is dark too. Without this a block whose text color is the unset
    // `--rdb-text-color` (and every `color: inherit` field in it) inherits the light text of a dark host theme and vanishes.
    color: TEXT_COLORS.TEXT_PRIMARY,
    // Paper is light: browser defaults inside it (the pale link colour, native controls, scrollbars) must be the light-scheme ones.
    colorScheme: 'light',
    backgroundColor: BACKGROUND_COLORS.WHITE,
    border: paperBorder,
    borderRadius: 2,
    boxShadow: paperShadow,
    ...pageBackgroundStyle,
    transform: shouldScale ? `scale(${scale})` : undefined,
    transformOrigin: shouldScale ? 'center top' : undefined,
    display: 'flex',
    flexDirection: 'column'
  };

  if (layoutMode === 'fixed') {
    sectionStyle.height = `${heightMm}mm`;
  } else {
    sectionStyle.minHeight = `${heightMm}mm`;
    sectionStyle.height = 'auto';
  }

  const pageBody = (
    <>
      <DocumentSheetTokens />
      {showHeaderLogo && logo && logoUrl ? (
        <CompanyLogoSlot url={logoUrl} size={logo.size} alignment={logo.alignment} slot="header" />
      ) : null}
      <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>{children}</div>
      {showFooterLogo && logo && logoUrl ? (
        <CompanyLogoSlot url={logoUrl} size={logo.size} alignment={logo.alignment} slot="footer" compact />
      ) : null}
    </>
  );

  if (!shouldScale) {
    return (
      // A group, not a landmark: every page carries the same label, and repeated landmarks fail axe's landmark-unique.
      <section
        style={sectionStyle}
        role="group"
        aria-label="document-page"
        data-testid="page-frame"
        data-document-sheet=""
        data-scale="1"
        data-page-number={pageNumber}
      >
        {pageBody}
      </section>
    );
  }

  return (
    <div
      data-testid="document-render:page:scale-wrapper"
      style={{
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: `${heightMm * scale}mm`,
        minHeight: layoutMode === 'flow' ? `${heightMm * scale}mm` : undefined,
        marginBottom: paperMarginBottom
      }}
    >
      <section
        style={{ ...sectionStyle, marginBottom: 0 }}
        role="group"
        aria-label="document-page"
        data-testid="page-frame"
        data-document-sheet=""
        data-scale={String(scale)}
        data-page-number={pageNumber}
      >
        {pageBody}
      </section>
    </div>
  );
}
